/**
 * Device ID management utilities
 * Provides secure device identification for patient portal authentication
 */

/**
 * Normalizes a device ID string by removing dashes and truncating to 32 characters
 * @param value - The device ID string to normalize
 * @returns Normalized device ID or null if invalid
 */
function normalizeDeviceId(value: string | null): string | null {
  if (!value) return null;

  const withoutDashes = value.replace(/-/g, '');
  const truncated = withoutDashes.slice(0, 32);

  return truncated.length > 0 ? truncated : null;
}

/**
 * Generates a cryptographically secure device ID
 * @returns A 32-character hexadecimal device ID
 */
function generateSecureDeviceId(): string {
  // Try modern crypto.randomUUID() first
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return (crypto as Crypto).randomUUID().replace(/-/g, '');
  }

  // Fallback to crypto.getRandomValues()
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    (crypto as Crypto).getRandomValues(bytes);
    return Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Final fallback to timestamp + random (less secure but functional)
  let deviceId = Date.now().toString(16) + Math.random().toString(16).slice(2);
  while (deviceId.length < 32) {
    deviceId += Math.random().toString(16).slice(2);
  }

  return deviceId.slice(0, 32);
}

/**
 * Device ID management class
 * Handles secure device identification with localStorage persistence
 */
class DeviceManager {
  private readonly storageKey = 'patient_portal_device_id';

  /**
   * Gets or creates a device ID for the current device
   * @returns Device ID string or null if unavailable
   */
  getOrCreateDeviceId(): string | null {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        return null;
      }

      // Try to get existing device ID
      const existing = window.localStorage.getItem(this.storageKey);
      const normalizedExisting = normalizeDeviceId(existing);

      if (normalizedExisting) {
        // Update storage if normalization changed the value
        if (existing !== normalizedExisting) {
          window.localStorage.setItem(this.storageKey, normalizedExisting);
        }
        return normalizedExisting;
      }

      // Generate new device ID
      const generated = generateSecureDeviceId();
      const normalizedGenerated = normalizeDeviceId(generated);

      if (normalizedGenerated) {
        window.localStorage.setItem(this.storageKey, normalizedGenerated);
        return normalizedGenerated;
      }

      return null;
    } catch (error) {
      console.warn('Failed to get or create device ID:', error);
      return null;
    }
  }

  /**
   * Clears the stored device ID
   */
  clearDeviceId(): void {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      console.warn('Failed to clear device ID:', error);
    }
  }

  /**
   * Gets the current device ID without creating a new one
   * @returns Current device ID or null if not set
   */
  getCurrentDeviceId(): string | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      const existing = window.localStorage.getItem(this.storageKey);
      return normalizeDeviceId(existing);
    } catch (error) {
      console.warn('Failed to get current device ID:', error);
      return null;
    }
  }
}

// Create and export a singleton instance
const device = new DeviceManager();

export default device;

// Export the class for advanced usage if needed
export { DeviceManager };

// Export individual functions for backward compatibility
export const getOrCreateDeviceId = () => device.getOrCreateDeviceId();
export const clearDeviceId = () => device.clearDeviceId();
export const getCurrentDeviceId = () => device.getCurrentDeviceId();